import { Client } from '@connectrpc/connect';

import { ProfileService } from './espb/profile_service_pb';
import { requireFieldNotEmpty } from './grpcUtils';
import { toSyncDto as toSyncDto } from './sync';
import {
  ForeignProfileDto,
  OrganizationId,
  ProfileDto,
  ProfileId,
  SyncUpdate,
  RegeneratedProfileDto,
  VaultCreationDto,
  Email,
  toPbProfileRegenerateData,
} from './types';
import { toPbVaultCreationData } from './vaults';

export default class ProfileServiceClient {
  constructor(private readonly espbClient: Client<typeof ProfileService>) {}

  async regenerate(profiles: Array<RegeneratedProfileDto>): Promise<SyncUpdate> {
    const resp = await this.espbClient.regenerate({
      profiles: profiles.map(toPbProfileRegenerateData),
    });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async list(profileIds: ProfileId[]): Promise<ForeignProfileDto[]> {
    const resp = await this.espbClient.list({ profileIds });
    return resp.profiles.map((p) => {
      const profile: ForeignProfileDto = {
        ...p,
        organizationId: p.organizationId || undefined,
        email: p.email || undefined,
      };
      return profile;
    });
  }

  async createPrivate(
    email: Email,
    privateProfile: ProfileDto,
    privateVault: VaultCreationDto,
  ): Promise<{
    privateProfileId: ProfileId;
    syncUpdate: SyncUpdate;
  }> {
    const resp = await this.espbClient.createPrivate({
      email,
      privateProfile,
      privateVault: toPbVaultCreationData(privateVault),
    });
    return {
      privateProfileId: requireFieldNotEmpty(resp.privateProfileId),
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
    };
  }

  async deletePrivate(privateProfileId: ProfileId): Promise<SyncUpdate> {
    const resp = await this.espbClient.deletePrivate({ privateProfileId });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async requestEmailChange(
    profileId: ProfileId,
    email: Email,
  ): Promise<{
    verificationEmailSent: boolean;
    syncUpdate: SyncUpdate;
  }> {
    const resp = await this.espbClient.requestEmailChange({ profileId, email });
    return {
      ...resp,
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
    };
  }

  async cancelEmailChange(profileId: ProfileId): Promise<SyncUpdate> {
    const resp = await this.espbClient.cancelEmailChange({ profileId });
    return toSyncDto(requireFieldNotEmpty(resp.syncUpdate));
  }

  async verifyEmailAddress(token: string): Promise<{
    email: string;
  }> {
    const response = await this.espbClient.verifyEmailAddress({ token });
    return {
      email: requireFieldNotEmpty(response.email),
    };
  }

  async getProfileInfo(profileId: ProfileId): Promise<{
    email: Email;
    organizationId: OrganizationId;
    organizationName: string;
    organizationIcon: string | undefined;
  }> {
    const resp = await this.espbClient.getProfileInfo({ profileId });
    return {
      email: resp.email,
      organizationId: resp.organizationId,
      organizationName: resp.organizationName,
      organizationIcon: resp.organizationIcon || undefined,
    };
  }

  async checkStartCode(options: {
    startCode: string;
    email?: Email;
    profileId?: ProfileId;
  }): Promise<void> {
    await this.espbClient.checkStartCode(options);
  }

  async claimWithStartCode(
    profileId: ProfileId,
    startCode: string,
  ): Promise<{ syncUpdate: SyncUpdate; organizationId: OrganizationId }> {
    const resp = await this.espbClient.claimWithStartCode({ profileId, startCode });
    return {
      syncUpdate: toSyncDto(requireFieldNotEmpty(resp.syncUpdate)),
      organizationId: resp.organizationId,
    };
  }
}
